import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { AuthContext } from '../shared/Context/auth-context';
import { useSessionState } from '../shared/hooks/session-state-hook';
import { sessionKey } from '../shared/util/session-storage';
import PageHeading from '../shared/components/UIElements/PageHeading';
import Icon from '../shared/components/UIElements/Icon';
import { useCareResource } from './care-api';
import { activeMember, entryStatus, formatCareDate, measurementText } from './care-utils';
import EntryComposer from './EntryComposer';
import EntryDiscussion from './EntryDiscussion';
import CarePlan from './CarePlan';
import VitalsChart from './VitalsChart';
import './Care.css';

const typeLabels = {
  vital: 'Vital reading',
  symptom: 'Patient update',
  question: 'Treatment question',
  event: 'Health event',
  prescription: 'Prescription',
  report: 'Health report',
  pharmacy: 'Pharmacy update'
};
const typeIcons = {
  vital: 'pulse',
  symptom: 'heart',
  question: 'bell',
  event: 'calendar',
  prescription: 'file',
  report: 'file',
  pharmacy: 'pharmacy'
};
const safeReportUrl = (fileUrl) => {
  if (
    typeof fileUrl !== 'string' ||
    !/^\/?uploads\/[\w\-./% ]+$/.test(fileUrl) ||
    fileUrl.includes('..')
  )
    return null;
  return `${(process.env.REACT_APP_BACKEND_URL || 'http://localhost:5006').replace(/\/$/, '')}/${fileUrl.replace(/^\//, '')}`;
};

const CareTimeline = () => {
  const auth = useContext(AuthContext);
  const params = useParams();
  const location = useLocation();
  const patientId = params.patientId || auth.patientId;
  const role = auth.isDoctorLoggedIn ? 'doctor' : auth.isPharmacyLoggedIn ? 'pharmacy' : 'patient';
  const actorId =
    role === 'doctor' ? auth.doctorId : role === 'pharmacy' ? auth.pharmacyId : auth.patientId;
  const key = (resource) => sessionKey(`${role}:${actorId}`, `care:${patientId}:${resource}`);
  const {
    data: record,
    loading,
    error,
    reload
  } = useCareResource(patientId ? `/patients/${patientId}` : null);
  const [tab, setTab] = useSessionState(key('tab'), 'timeline');
  const [filter, setFilter] = useSessionState(key('filter'), 'all');
  const [expanded, setExpanded] = useSessionState(key('expanded'), '');
  const [composer, setComposer] = useSessionState(key('composer'), '');
  const [relatedEntryId, setRelatedEntryId] = useSessionState(key('related-entry'), '');
  const [notice, setNotice] = useState('');
  const [limit, setLimit] = useState(30);
  const entries = useMemo(
    () =>
      [...(record?.entries || [])].sort(
        (a, b) => new Date(b.occurredAt || b.createdAt) - new Date(a.occurredAt || a.createdAt)
      ),
    [record]
  );
  const queryEntry = new URLSearchParams(location.search).get('entry');
  useEffect(() => {
    if (queryEntry) {
      setExpanded(queryEntry);
      setTab('timeline');
      setFilter('all');
    }
  }, [queryEntry, setExpanded, setTab, setFilter]);
  useEffect(() => {
    if (expanded && record && !loading)
      document
        .getElementById(`care-entry-${expanded}`)
        ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [expanded, record, loading]);
  const saved = (message) => {
    setNotice(message);
    setComposer('');
    reload();
  };
  if (loading && !record)
    return (
      <div className="care-panel" role="status">
        Loading the care record…
      </div>
    );
  if (!record)
    return (
      <div className="care-panel">
        <h1>Care timeline</h1>
        <p className="care-error" role="alert">
          {error || 'This care record is unavailable.'}
        </p>
        <button className="button" onClick={reload}>
          Try again
        </button>
      </div>
    );
  const patient = record.viewer.role === 'patient';
  const scopes = record.viewer.scopes || [];
  const primary = (record.members || []).find(
    (member) => member.role === 'doctor' && member.primary && activeMember(member)
  );
  const pending = entries.filter(
    (entry) => !entry.legacy && ['pending', 'needs-information'].includes(entry.status)
  );
  const unreadFeedback = entries.filter(
    (entry) =>
      !entry.acknowledgedAt &&
      entry.responses?.some((response) => response.author?.role === 'doctor')
  );
  const visible = entries.filter(
    (entry) =>
      filter === 'all' ||
      (filter === 'feedback'
        ? unreadFeedback.some((item) => item.id === entry.id)
        : filter === 'pending'
          ? !entry.legacy && ['pending', 'needs-information'].includes(entry.status)
          : entry.type === filter)
  );
  // Keep a deep-linked update visible even if it is older than the first page.
  const shown = visible.slice(
    0,
    Math.max(limit, expanded ? visible.findIndex((entry) => entry.id === expanded) + 1 : 0)
  );
  const canReadVitals = patient || scopes.includes('vitals');
  const canRecord = patient || (role === 'doctor' && scopes.includes('vitals'));
  const canEvent =
    patient ||
    (role === 'doctor' && scopes.includes('timeline')) ||
    (role === 'pharmacy' && scopes.includes('medications'));
  const canPlan = patient || scopes.includes('updates');
  const openComposer = (type, related = '') => {
    setRelatedEntryId(related);
    setComposer(type);
    setTab('timeline');
    setNotice('');
  };
  const relatedSource = entries.find((entry) => entry.id === relatedEntryId);
  const openDiscussion = (id) => {
    setExpanded(id);
    setFilter('all');
  };
  return (
    <div className="care-workspace">
      <PageHeading
        eyebrow="CARE, BETWEEN VISITS"
        title={patient ? 'Your care timeline' : `${record.patient.name}’s care timeline`}
        description="The record, the changes, and the next step — together."
      >
        <button className="button button--inverse" onClick={reload} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        {role === 'doctor' && (
          <Link className="button" to={`/${auth.doctorId}/prescribe/${patientId}`}>
            Create prescription
          </Link>
        )}
        {patient ? (
          <Link className="button button--inverse" to="/care/team">
            My care team
          </Link>
        ) : (
          <Link className="button button--inverse" to="/care/inbox">
            Back to review inbox
          </Link>
        )}
      </PageHeading>
      {error && (
        <p className="care-error" role="alert">
          {error}{' '}
          <button className="care-text-button" onClick={reload}>
            Retry
          </button>
        </p>
      )}
      {notice && (
        <p className="care-notice" role="status">
          {notice}
        </p>
      )}
      {patient && unreadFeedback.length > 0 && (
        <div className="care-notice care-feedback-notice">
          <span>
            {unreadFeedback.length} {unreadFeedback.length === 1 ? 'update has' : 'updates have'}{' '}
            feedback for you to read.
          </span>
          <button
            className="care-text-button"
            onClick={() => {
              setTab('timeline');
              setFilter('feedback');
              setComposer('');
              setExpanded(unreadFeedback[0].id);
            }}
          >
            Read feedback
          </button>
        </div>
      )}
      <section className="care-patient-banner" aria-label="Patient context">
        <div className="care-patient-identity">
          <span className="care-avatar">
            <Icon name="patient" size={27} />
          </span>
          <div>
            <h2>{record.patient.name}</h2>
            <p>
              {[record.patient.age ? `${record.patient.age} years` : '', record.patient.gender]
                .filter(Boolean)
                .join(' · ') || 'Patient care record'}
            </p>
          </div>
        </div>
        <div>
          <span className="care-meta-label">Primary reviewer</span>
          <strong>
            {primary?.name || (record.primaryDoctorId ? 'Primary doctor assigned' : 'Not selected')}
          </strong>
          <p>
            {record.primaryDoctorId
              ? 'New updates can be sent to this doctor.'
              : 'Choose a reviewer when you share an update.'}
          </p>
        </div>
        {record.patient.allergies && (
          <div>
            <span className="care-meta-label">Recorded allergies</span>
            <strong>{record.patient.allergies}</strong>
            <p>Confirm current information with your care team.</p>
          </div>
        )}
      </section>
      <div className="care-summary-grid">
        <div>
          <span className="care-meta-label">Updates awaiting action</span>
          <strong>{pending.length}</strong>
          <span>Review or more information needed</span>
        </div>
        <div>
          <span className="care-meta-label">Latest timeline event</span>
          <strong className="care-summary-date">
            {entries.length ? formatCareDate(entries[0].occurredAt, false) : 'No entries yet'}
          </strong>
          <span>
            {entries[0]
              ? typeLabels[entries[0].type] || 'Care update'
              : 'Start with a reading or update'}
          </span>
        </div>
        <div>
          <span className="care-meta-label">Follow-up tasks</span>
          <strong>
            {(record.tasks || []).filter((task) => task.status !== 'completed').length}
          </strong>
          <span>Pending steps in the care plan</span>
        </div>
      </div>
      <nav className="care-tabs" aria-label="Care record sections">
        <button
          aria-current={tab === 'timeline' ? 'page' : undefined}
          onClick={() => setTab('timeline')}
        >
          <Icon name="history" size={18} />
          Timeline
        </button>
        {canReadVitals && (
          <button
            aria-current={tab === 'vitals' ? 'page' : undefined}
            onClick={() => setTab('vitals')}
          >
            <Icon name="chart" size={18} />
            Vitals & trends
          </button>
        )}
        {canPlan && (
          <button aria-current={tab === 'plan' ? 'page' : undefined} onClick={() => setTab('plan')}>
            <Icon name="calendar" size={18} />
            Care plan
          </button>
        )}
      </nav>
      {tab === 'vitals' && canReadVitals ? (
        <>
          {canRecord && (
            <div className="care-actions care-section-actions">
              <button className="button" onClick={() => openComposer('vital')}>
                <Icon name="plus" size={17} /> Record a reading
              </button>
            </div>
          )}
          <VitalsChart
            entries={entries}
            events={entries.filter((entry) =>
              ['prescription', 'event', 'report'].includes(entry.type)
            )}
            storageKey={key('chart')}
          />
        </>
      ) : tab === 'plan' && canPlan ? (
        <CarePlan record={record} onSaved={saved} />
      ) : (
        <>
          {composer &&
          ((composer === 'vital' && canRecord) || (composer !== 'vital' && canEvent)) ? (
            <EntryComposer
              key={`${composer}:${relatedEntryId}`}
              record={record}
              initialType={composer}
              relatedEntryId={relatedSource ? relatedEntryId : ''}
              initialTitle={
                relatedSource ? `Question about ${relatedSource.title}`.slice(0, 160) : ''
              }
              onClose={() => setComposer('')}
              onSaved={saved}
            />
          ) : (
            <>
              <div className="care-panel care-timeline-toolbar">
                <div>
                  <h2>Patient history & updates</h2>
                  <p className="care-muted">Dated records with their source and review status.</p>
                </div>
                <div className="care-actions">
                  {canRecord && (
                    <button className="button" onClick={() => openComposer('vital')}>
                      <Icon name="plus" size={17} /> Record a reading
                    </button>
                  )}
                  {canEvent && (
                    <button
                      className="button button--inverse"
                      onClick={() =>
                        openComposer(
                          role === 'pharmacy' ? 'pharmacy' : role === 'doctor' ? 'event' : 'symptom'
                        )
                      }
                    >
                      {' '}
                      {role === 'pharmacy' ? 'Add pharmacy update' : 'Share an update'}
                    </button>
                  )}
                </div>
              </div>
              <div className="care-filter-row">
                <label htmlFor="care-timeline-filter">
                  Show
                  <select
                    id="care-timeline-filter"
                    value={filter}
                    onChange={(event) => {
                      setFilter(event.target.value);
                      setLimit(30);
                    }}
                  >
                    <option value="all">All shared entries</option>
                    <option value="pending">Awaiting action</option>
                    {patient && <option value="feedback">Feedback to read</option>}
                    {Object.entries(typeLabels)
                      .filter(([type]) => entries.some((entry) => entry.type === type))
                      .map(([type, label]) => (
                        <option key={type} value={type}>
                          {label}
                        </option>
                      ))}
                  </select>
                </label>
                <span className="care-muted">
                  {visible.length} {visible.length === 1 ? 'entry' : 'entries'}
                </span>
              </div>
              {!visible.length && (
                <div className="care-panel care-empty">
                  <Icon name="history" size={32} />
                  <h3>
                    {entries.length ? 'No entries match this filter' : 'Start building the picture'}
                  </h3>
                  <p>
                    {entries.length
                      ? 'Choose another category to see more of the timeline.'
                      : 'Consultations and reports appear here alongside new readings and updates. Historical vitals appear only when dated readings have been recorded.'}
                  </p>
                </div>
              )}
              <ol className="care-timeline">
                {shown.map((entry) => {
                  const isOpen = expanded === entry.id;
                  const fileUrl = safeReportUrl(entry.fileUrl);
                  return (
                    <li
                      key={entry.id}
                      id={`care-entry-${entry.id}`}
                      className="care-timeline-entry"
                    >
                      <span className={`care-event-icon care-event-icon--${entry.type}`}>
                        <Icon name={typeIcons[entry.type] || 'file'} size={19} />
                      </span>
                      <article className="care-panel">
                        <div className="care-entry-meta">
                          <span>{typeLabels[entry.type] || 'Care update'}</span>
                          <time dateTime={entry.occurredAt}>
                            {formatCareDate(entry.occurredAt)}
                          </time>
                          <span
                            className={`care-pill ${['pending', 'needs-information'].includes(entry.status) ? 'care-pill--attention' : ''}`}
                          >
                            {entryStatus(entry)}
                          </span>
                        </div>
                        <h3>{entry.title}</h3>
                        {entry.relatedEntryId && (
                          <p className="care-muted">
                            Related record:{' '}
                            {entries.some((source) => source.id === entry.relatedEntryId) ? (
                              <button
                                className="care-text-button"
                                onClick={() => openDiscussion(entry.relatedEntryId)}
                              >
                                {entries.find((source) => source.id === entry.relatedEntryId).title}
                              </button>
                            ) : (
                              'Not included in your shared access'
                            )}
                          </p>
                        )}
                        {entry.measurement && (
                          <div className="care-reading">
                            <strong>{measurementText(entry.measurement)}</strong>
                            {entry.measurement.context && (
                              <span>{entry.measurement.context.replace(/-/g, ' ')}</span>
                            )}
                          </div>
                        )}
                        {entry.body && <p className="care-prewrap">{entry.body}</p>}
                        {!!entry.medications?.length && (
                          <ul className="care-medications">
                            {entry.medications.map((medicine, index) => (
                              <li key={index}>
                                <strong>{medicine.name}</strong> · {medicine.dose} ·{' '}
                                {medicine.frequency} · {medicine.duration}
                              </li>
                            ))}
                          </ul>
                        )}
                        {entry.type === 'prescription' && (
                          <p className="care-muted">
                            Prescription history. Confirm with your clinician which medicines are
                            currently active.
                          </p>
                        )}
                        <p className="care-entry-source">
                          Recorded by {entry.author?.name || 'Care record'}
                          {entry.author?.role ? ` · ${entry.author.role}` : ''}
                          {entry.createdAt && entry.createdAt !== entry.occurredAt
                            ? ` · Added ${formatCareDate(entry.createdAt)}`
                            : ''}
                        </p>
                        <div className="care-entry-footer">
                          <span className="care-muted">
                            {entry.reviewerId
                              ? `Reviewer: ${entry.reviewerName || 'Assigned doctor'}`
                              : entry.legacy
                                ? 'From your existing record'
                                : 'No reviewer assigned'}
                          </span>
                          <div className="care-actions">
                            {fileUrl && (
                              <a href={fileUrl} target="_blank" rel="noreferrer">
                                View report <Icon name="arrow" size={15} />
                              </a>
                            )}
                            {entry.legacy && patient && (
                              <button
                                className="care-text-button"
                                onClick={() => openComposer('question', entry.id)}
                              >
                                Ask about this {entry.type === 'report' ? 'report' : 'prescription'}
                              </button>
                            )}
                            {!entry.legacy && (
                              <button
                                className="care-text-button"
                                aria-expanded={isOpen}
                                aria-controls={`care-discussion-${entry.id}`}
                                onClick={() => setExpanded(isOpen ? '' : entry.id)}
                              >
                                {isOpen
                                  ? 'Close discussion'
                                  : `Feedback & review${entry.responses?.length ? ` (${entry.responses.length})` : ''}`}
                              </button>
                            )}
                          </div>
                        </div>
                        {entries.some((item) => item.relatedEntryId === entry.id) && (
                          <div className="care-linked-discussions">
                            <span className="care-muted">Related updates</span>
                            {entries
                              .filter((item) => item.relatedEntryId === entry.id)
                              .map((item) => (
                                <button
                                  key={item.id}
                                  className="care-text-button"
                                  onClick={() => openDiscussion(item.id)}
                                >
                                  {item.title} · {entryStatus(item)}
                                </button>
                              ))}
                          </div>
                        )}
                        {isOpen && !entry.legacy && (
                          <div id={`care-discussion-${entry.id}`}>
                            <EntryDiscussion entry={entry} record={record} onSaved={saved} />
                          </div>
                        )}
                      </article>
                    </li>
                  );
                })}
              </ol>
              {shown.length < visible.length && (
                <button className="button button--inverse" onClick={() => setLimit(limit + 30)}>
                  Show older entries
                </button>
              )}
            </>
          )}
        </>
      )}
      <p className="care-footnote">
        Updates are reviewed during your clinic’s working hours, according to its agreed response
        times. For urgent help, contact your clinic or local emergency service directly.
      </p>
    </div>
  );
};
export default CareTimeline;
